// Core TypeScript interfaces and types for GeoMarkup
import {
  NOTIFICATION_AUTO_HIDE_DURATION,
  DEFAULT_MAP_ZOOM,
  MIN_LATITUDE,
  MAX_LATITUDE,
  MIN_LONGITUDE,
  MAX_LONGITUDE,
} from '../constants';
import type { TaskGeometry } from './geometry';

// ===== ENUMS =====

export enum TaskStatus {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Delayed = 'Delayed',
  Completed = 'Completed',
  Archived = 'Archived',
}

export enum Priority {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Urgent = 'Urgent',
}

export enum MarkerShape {
  Circle = 'Circle',
  Square = 'Square',
  Triangle = 'Triangle',
}

export enum NotificationType {
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
  Info = 'info',
}

/** Task form mode - determines create vs edit behavior */
export enum TaskFormMode {
  Create = 'create',
  Edit = 'edit',
}

/** Artifact view mode — shared across tasks, notes, and future artifacts */
export const VIEW_MODE = {
  MAP: 'map',
  LIST: 'list',
  KANBAN: 'kanban',
} as const;
export type ViewMode = (typeof VIEW_MODE)[keyof typeof VIEW_MODE];

/** Notes only support the map and list subsets of `VIEW_MODE`. */
export const NOTE_VIEW_MODE = {
  MAP: VIEW_MODE.MAP,
  LIST: VIEW_MODE.LIST,
} as const;
export type NoteViewMode = (typeof NOTE_VIEW_MODE)[keyof typeof NOTE_VIEW_MODE];

// ===== CORE INTERFACES =====

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface Requirement {
  id: string;
  text: string;
  completed: boolean;
  order: number;
  createdAt: Date;
}

export interface Attachment {
  id: string;
  name: string;
  type: string;
  size: number;
  data: string; // base64 for in-memory storage
  uploadedAt: Date;
}

export interface TaskComment {
  id: string;
  message: string;
  author: string;
  authorId: string;
  authorAvatarUrl?: string | null;
  createdAt: Date;
  updatedAt?: Date;
  isEdited?: boolean;
  isDeleted?: boolean;
}

export enum TaskHistoryEventType {
  TaskCreated = 'TaskCreated',
  TaskUpdated = 'TaskUpdated',
  TaskDeleted = 'TaskDeleted',
  TitleChanged = 'TitleChanged',
  DescriptionChanged = 'DescriptionChanged',
  LocationChanged = 'LocationChanged',
  StatusChanged = 'StatusChanged',
  PriorityChanged = 'PriorityChanged',
  MarkerShapeChanged = 'MarkerShapeChanged',
  MarkerColorChanged = 'MarkerColorChanged',
  MarkerIconChanged = 'MarkerIconChanged',
  RequirementAdded = 'RequirementAdded',
  RequirementUpdated = 'RequirementUpdated',
  RequirementDeleted = 'RequirementDeleted',
  RequirementCompleted = 'RequirementCompleted',
  RequirementUncompleted = 'RequirementUncompleted',
  AttachmentAdded = 'AttachmentAdded',
  AttachmentDeleted = 'AttachmentDeleted',
  CommentAdded = 'CommentAdded',
  CommentUpdated = 'CommentUpdated',
  CommentDeleted = 'CommentDeleted',
  AssigneeChanged = 'AssigneeChanged',
  DueDateChanged = 'DueDateChanged',
  StartDateChanged = 'StartDateChanged',
  RelatedTaskAdded = 'RelatedTaskAdded',
  RelatedTaskRemoved = 'RelatedTaskRemoved',
  RequireRelatedTasksChanged = 'RequireRelatedTasksChanged',

  // Map representation changes (simplified for multi-geometry support)
  MapRepresentationChanged = 'MapRepresentationChanged',
}

export interface TaskHistoryEvent {
  id: string;
  taskId: string;
  eventType: TaskHistoryEventType;
  description: string;
  author: string;
  authorId?: string;
  authorAvatarUrl?: string | null;
  createdAt: Date;
  metadata?: {
    oldValue?: string | number | boolean | Date | null;
    newValue?: string | number | boolean | Date | null;
    fieldName?: string;
    [key: string]: string | number | boolean | Date | null | undefined;
  };
}

export interface RelatedTaskSummary {
  id: string;
  shortId: string;
  title: string;
  status: TaskStatus;
  location: string | null;
  dueDate: Date | null;
}

export interface Task {
  id: string;
  shortId: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: Priority;
  assignedToId: string | null;
  assignedToName: string | null;
  assignedToAvatarUrl?: string | null;
  assignedToIsFormerMember: boolean;
  startDate: Date | null;
  dueDate: Date | null;
  completedAt: Date | null;
  location?: string; // Optional location name (road, address, or custom)

  /**
   * Anchor point for GPS detection and task location
   *
   * This is the Single Source of Truth (SSOT) for task location:
   * - GPS detection and reverse geocoding
   * - Zoom to task from list
   * - Task positioning on map
   * - Always draggable in create/edit mode
   *
   * Separate from geometry centers which are used for rendering and individual geometry zoom.
   *
   * @since 3.1.0
   */
  anchorPoint: Coordinates;

  /**
   * Geometric representations of the task on the map
   *
   * Array of geometries allowing multiple shapes per task:
   * - **Marker**: Single point with shape, color, and icon
   * - **Line**: Multi-point polyline with style, width, and color
   * - **Shape**: Polygon (rectangle, circle, triangle) with fill and border
   *
   * Each geometry has a `calculatedCenter` field with GPS coordinates used for:
   * - Location/road detection
   * - Map centering operations
   * - Task positioning on the map
   *
   * @since 3.0.0 - Changed from single geometry to array for multi-shape support
   * @since 2.0.0 - Replaced deprecated fields: coordinates, shape, color, icon
   */
  geometries: TaskGeometry[];

  requirements: Requirement[];
  attachments: Attachment[];
  comments: TaskComment[];
  history: TaskHistoryEvent[];
  relatedTasks: RelatedTaskSummary[]; // Related task summaries from backend
  requireRelatedTasksCompletion: boolean; // Require all related tasks to be completed before this task can be marked as complete
  requirementCount?: number;
  completedRequirementCount?: number;
  attachmentCount?: number;
  commentCount?: number;
  createdAt: Date;
  updatedAt: Date | null;
}

// ===== STATE MANAGEMENT INTERFACES =====

export interface PreviewMarker {
  coordinates: Coordinates;
  shape: MarkerShape;
  color?: string;
  icon?: string;
  isVisible: boolean;
}

// NOTE: AppState removed - project migrated to Zustand for state management
// All state is now managed through Zustand store slices (see src/store/)

// ===== MARKER ICON TYPES =====
// Re-export marker icon types for convenient access
export { MarkerIcon, ICON_CATEGORIES, ICON_REGISTRY } from './MarkerIcon';
export type { MarkerIconType, IconCategory, IconMetadata } from './MarkerIcon';
export { getIconsByCategory, isValidIconKey } from './MarkerIcon';

// ===== ENHANCED HISTORY TYPES =====
// Re-export enhanced history types for convenient access
export type {
  EnhancedTaskHistoryEvent,
  HistoryEventMetadata,
  FieldChange,
  TaskComparison,
  HistoryEventGroup,
  CreateHistoryEventOptions,
  HistoryFilterOptions,
} from './history';

export type { ExtendedTaskHistoryEventType } from './history';

// ===== ACTION TYPES =====

export type TaskAction =
  | {
      type: 'CREATE_TASK';
      payload: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>;
    }
  | {
      type: 'UPDATE_TASK';
      payload: { id: string; updates: Partial<Task>; batchId?: string };
    }
  | { type: 'DELETE_TASK'; payload: string }
  | { type: 'SET_TASK_STATUS'; payload: { id: string; status: TaskStatus } }
  | { type: 'SELECT_TASK'; payload: string | null }
  | {
      type: 'TOGGLE_TASK_FORM';
      payload?: { open?: boolean; mode?: TaskFormMode | null };
    }
  | { type: 'SET_TASK_FORM_MODE'; payload: TaskFormMode | null }
  | { type: 'TOGGLE_TASK_DETAILS'; payload?: boolean }
  | { type: 'TOGGLE_SIDEBAR'; payload?: boolean }
  | { type: 'TOGGLE_TASKS_PANEL'; payload?: boolean }
  | {
      type: 'ADD_REQUIREMENT';
      payload: {
        taskId: string;
        requirement: Omit<Requirement, 'id' | 'createdAt'>;
        batchId?: string;
      };
    }
  | {
      type: 'UPDATE_REQUIREMENT';
      payload: {
        taskId: string;
        requirementId: string;
        updates: Partial<Requirement>;
        batchId?: string;
      };
    }
  | {
      type: 'DELETE_REQUIREMENT';
      payload: {
        taskId: string;
        requirementId: string;
        batchId?: string;
      };
    }
  | {
      type: 'ADD_ATTACHMENT';
      payload: {
        taskId: string;
        attachment: Omit<Attachment, 'id' | 'uploadedAt'>;
        batchId?: string;
      };
    }
  | {
      type: 'DELETE_ATTACHMENT';
      payload: {
        taskId: string;
        attachmentId: string;
        batchId?: string;
      };
    }
  | {
      type: 'ADD_COMMENT';
      payload: {
        taskId: string;
        comment: Omit<TaskComment, 'id' | 'createdAt'>;
      };
    }
  | {
      type: 'UPDATE_COMMENT';
      payload: {
        taskId: string;
        commentId: string;
        updates: Partial<TaskComment>;
      };
    }
  | {
      type: 'DELETE_COMMENT';
      payload: { taskId: string; commentId: string };
    }
  | {
      type: 'ADD_HISTORY_EVENT';
      payload: {
        taskId: string;
        event: Omit<TaskHistoryEvent, 'id' | 'createdAt'>;
      };
    }
  | {
      type: 'ADD_ENHANCED_HISTORY_EVENT';
      payload: {
        taskId: string;
        event: TaskHistoryEvent;
      };
    }
  | {
      type: 'ADD_BATCH_HISTORY_EVENTS';
      payload: {
        taskId: string;
        events: TaskHistoryEvent[];
        batchId?: string;
      };
    }
  | {
      type: 'CREATE_HISTORY_FROM_TASK_CHANGES';
      payload: {
        taskId: string;
        oldTask: Task | null;
        newTask: Task;
        author: string;
        customDescription?: string;
        tags?: string[];
      };
    }
  | { type: 'SET_TEMP_COORDINATES'; payload: Coordinates | null }
  | { type: 'SET_TEMP_ROAD'; payload: string | null }
  | { type: 'ZOOM_TO_COORDINATES'; payload: Coordinates | null }
  | {
      type: 'SHOW_NAVIGATION_DIALOG';
      payload: { taskId: string; coordinates: Coordinates };
    }
  | { type: 'HIDE_NAVIGATION_DIALOG' }
  | { type: 'SET_CURRENT_USER'; payload: string };
// NOTE: Legacy storage actions removed (LOAD_STATE, STORAGE_ERROR)
// Project now uses Zustand with persist middleware

// ===== ROAD TYPES =====

// Re-export road types for convenient access
export type { Road } from './road';

// ===== GEOMETRY TYPES =====

// Re-export geometry types for convenient access
export * from './geometry';

// ===== HISTORY TYPES =====

// Re-export history types for convenience
export * from './history';

// ===== AUTH TYPES =====

// Re-export auth types for convenient access
export * from './auth';

// ===== WORKSPACE TYPES =====

// Re-export workspace types for convenient access
export * from './workspace';

// ===== PRESENCE TYPES =====

// Re-export presence types (live location sharing) for convenient access
export type { MemberLocation } from './presence';

// ===== NOTES TYPES =====

// Re-export notes types for convenient access
export * from './notes';

// ===== OBSTRUCTIONS TYPES =====

// Re-export obstructions types for convenient access
export * from './obstructions';

// ===== SHARE TYPES =====

// Re-export share / public-share types for convenient access
export * from './share';

// ===== TASK LIST ITEM =====

// Lightweight task for list views (lazy loading / pagination)
export type { TaskListItem } from './TaskListItem';

// ===== UTILITY TYPES =====

export type CreateTaskInput = Omit<
  Task,
  | 'id'
  | 'createdAt'
  | 'updatedAt'
  | 'requirements'
  | 'attachments'
  | 'comments'
  | 'history'
> & {
  requirements?: Omit<Requirement, 'id' | 'createdAt'>[];
  attachments?: Omit<Attachment, 'id' | 'uploadedAt'>[];
  comments?: Omit<TaskComment, 'id' | 'createdAt'>[];
};

export type UpdateTaskInput = Partial<
  Omit<Task, 'id' | 'createdAt' | 'updatedAt'>
>;

export type TaskFormData = {
  title: string;
  description: string;
  priority: Priority;
  assignedToId: string | null;
  startDate: string; // ISO string for form handling
  dueDate: string; // ISO string for form handling
  shape: MarkerShape;
  color?: string; // Optional marker color
  coordinates: Coordinates;
};

// ===== TYPE GUARDS =====

export const isValidCoordinates = (coords: unknown): coords is Coordinates => {
  if (!coords || typeof coords !== 'object' || coords === null) {
    return false;
  }

  const obj = coords as Record<string, unknown>;
  return (
    typeof obj.lat === 'number' &&
    typeof obj.lng === 'number' &&
    obj.lat >= MIN_LATITUDE &&
    obj.lat <= MAX_LATITUDE &&
    obj.lng >= MIN_LONGITUDE &&
    obj.lng <= MAX_LONGITUDE
  );
};

export const isValidTaskStatus = (status: unknown): status is TaskStatus => {
  return Object.values(TaskStatus).includes(status as TaskStatus);
};

export const isValidPriority = (priority: unknown): priority is Priority => {
  return Object.values(Priority).includes(priority as Priority);
};

export const isValidMarkerShape = (shape: unknown): shape is MarkerShape => {
  return Object.values(MarkerShape).includes(shape as MarkerShape);
};

// ===== ADDRESS SEARCH INTERFACES =====

export interface AddressSearchResult {
  id: string;
  displayName: string;
  address: string;
  coordinates: Coordinates;
  type: string; // e.g., 'house', 'road', 'city', etc.
  importance: number; // 0-1 score from Nominatim
  boundingBox?: [number, number, number, number]; // [minLat, maxLat, minLng, maxLng]
  place_id?: string; // Nominatim place ID
  distance?: number; // Distance from user location in km
}

export interface GeocodingResult {
  place_id: string;
  licence: string;
  osm_type: string;
  osm_id: string;
  lat: string;
  lon: string;
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    county?: string;
    state?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
  boundingbox: [string, string, string, string];
  type: string;
  importance: number;
}

export interface AddressSearchProps {
  onLocationSelect: (result: AddressSearchResult) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  clearOnSelect?: boolean;
  maxResults?: number;
}

export interface AddressSearchError {
  type: 'network' | 'rate_limit' | 'parse' | 'unknown';
  message: string;
  retryAfter?: number; // seconds to wait before retry
}

// ===== MAP EVENT TYPES =====

export * from './mapEvents';

// ===== FORM TYPES =====

export * from './forms';

// ===== FILE VALIDATION TYPES =====

export * from './fileValidation';

// ===== LOADING TYPES =====

export * from './loading';

// ===== API TYPES =====

export * from './api';

// ===== CONSTANTS =====

export const TASK_CONSTANTS = {
  MAX_FILE_SIZE: 25 * 1024 * 1024, // 25MB
  SUPPORTED_FILE_TYPES: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/pdf',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  DEFAULT_MAP_CENTER: { lat: 52.0693, lng: 19.4803 } as Coordinates, // Poland center
  DEFAULT_MAP_ZOOM,
  NOTIFICATION_AUTO_HIDE_DURATION,
} as const;

export const PRIORITY_COLORS = {
  [Priority.Low]: '#868E96', // Mantine gray-6
  [Priority.Medium]: '#FAB005', // Mantine yellow-6
  [Priority.High]: '#FD7E14', // Mantine orange-6
  [Priority.Urgent]: '#FA5252', // Mantine red-6
} as const;

export const STATUS_COLORS = {
  [TaskStatus.Pending]: 'var(--app-status-pending)',
  [TaskStatus.InProgress]: 'var(--app-status-progress)',
  [TaskStatus.Delayed]: 'var(--app-status-delayed)',
  [TaskStatus.Completed]: 'var(--app-status-completed)',
  [TaskStatus.Archived]: 'var(--app-status-archived)',
} as const;
